import {
    ErrorHandler,
    inject,
    Injectable,
    InjectionToken,
    Injector,
    ModuleWithProviders,
    NgModule,
} from '@angular/core';
import { Store } from '@ngrx/store';
import * as Sentry from '@sentry/angular';
import { ErrorEvent } from '@sentry/core/build/types';

import { CONFIG } from '../config';
import { PublicSystemOptions } from '../interfaces/entity-interfaces';
import { CurrUser } from '../interfaces/object-interfaces';
import { getCurrUser } from '../store/curr-user';
import { getPublicSystemOptions } from '../store/system-options';
import { isLocalhost } from '../utils';

export interface SentryModuleConfig {
    project: 'frontend' | 'pos' | 'shop' | 'health' | 'smarttag' | 'master' | 'kitchen';
    preventTracingIntegration?: boolean;
}

export const SENTRY_MODULE_CONFIG = new InjectionToken<SentryModuleConfig>('SENTRY_MODULE_CONFIG');

// Regexes to detect lazy-chunk / dynamic import failures. Use regex literals
// (do NOT wrap the pattern in additional '/' characters when using RegExp constructor)
const chunkLoadFailErrorRegExp = /Loading chunk \d+ failed/;
const chunkFetchFailErrorRegExp = /Failed to fetch dynamically imported module: */;

@Injectable({ providedIn: 'root' })
export class SentryErrorHandler implements ErrorHandler {
    private sentryConfig = inject<SentryModuleConfig>(SENTRY_MODULE_CONFIG);

    private inj = inject(Injector);

    currUser: CurrUser;
    store: Store;
    publicSystemOptions: PublicSystemOptions;

    constructor() {
        const inj = this.inj;

        setTimeout(() => {
            this.store = inj.get(Store);
            this.store.select(getCurrUser).subscribe((value) => (this.currUser = value));
            this.store.select(getPublicSystemOptions).subscribe((value) => (this.publicSystemOptions = value));
        });

        this.initSentry();
    }
    handleError(error: any): any {
        // Normalise error message (some errors are strings, some are Error objects)
        const errorMsg = error?.message ?? (typeof error === 'string' ? error : error?.toString?.() ?? String(error));

        // lazy load / dynamic import failures - check regexes, rejection flags and known substrings
        if (
            chunkLoadFailErrorRegExp.test(errorMsg) ||
            chunkFetchFailErrorRegExp.test(errorMsg) ||
            (error?.rejection && error.rejection.loadChunkError) ||
            errorMsg.includes('Failed to fetch dynamically imported module') ||
            errorMsg.includes('Importing a module script failed')
        ) {
            window.location.reload();
            return null;
        }

        console.warn(`Error in project "${this.sentryConfig.project}, url: ${window.location.href}"`);

        if (this.currUser && this.currUser.person) {
            Sentry.setUser({
                id: this.currUser.person.id.toString(),
                email: this.currUser.person.email,
                username: this.currUser.person.name,
            });
        }

        if (this.publicSystemOptions) {
            Sentry.setTag('proman-version', this.publicSystemOptions.version);
            Sentry.setTag('proman-namespace', this.publicSystemOptions.namespace);
            Sentry.setTag('proman-domain', this.publicSystemOptions.domain);
        }

        Sentry.captureException(error.originalError || error);

        throw error;
    }

    initSentry() {
        const sentryIntegrations = [];
        if (!this.sentryConfig.preventTracingIntegration) {
            sentryIntegrations.push(
                Sentry.replayIntegration({
                    maskAllText: false,
                    maskAllInputs: false,
                    blockAllMedia: false,
                    networkDetailAllowUrls: [window.location.origin],
                })
            );
            // sentryIntegrations.push(Sentry.browserTracingIntegration());
        }
        Sentry.init({
            dsn: 'https://5249f0f475b543879158d130145f19a2@sentry.io/176946',
            beforeSend(event, hint): Promise<ErrorEvent | null> | ErrorEvent | null {
                const error = hint.originalException;
                const httpErrorsStatusesToCatch = [0, 400, 401, 403, 404, 409, 500, 502, 504];

                if (window.location.host.includes('localhost')) {
                    return;
                }
                if (event.request?.url.includes('/login')) {
                    return;
                } //Expired token filter.

                if (chunkLoadFailErrorRegExp.test(error?.toString())) {
                    window.location.reload();
                    return null;
                }

                if (chunkFetchFailErrorRegExp.test(error?.toString())) {
                    window.location.reload();
                    return null;
                }

                if (httpErrorsStatusesToCatch.includes((<any>error)?.status)) {
                    return null;
                }
                if (error?.toString().includes('Uncaught (in promise)')) {
                    return null;
                }

                return event;
            },
            tracesSampleRate: isLocalhost() ? 1.0 : 0.3,
            tracePropagationTargets: [CONFIG.api],
            replaysSessionSampleRate: 0,
            replaysOnErrorSampleRate: 1.0,
            integrations: sentryIntegrations,
        });
    }
}

@NgModule({
    providers: [{ provide: ErrorHandler, useClass: SentryErrorHandler, deps: [SENTRY_MODULE_CONFIG, Injector] }],
})
export class SentryModule {
    static forRoot(config: SentryModuleConfig): ModuleWithProviders<SentryModule> {
        return {
            ngModule: SentryModule,
            providers: [
                {
                    provide: SENTRY_MODULE_CONFIG,
                    useValue: config,
                },
            ],
        };
    }
}
